/** @format */

import {ArrowCircleUpIcon} from '@heroicons/react/solid'
import clsx from 'clsx'
import React, {ComponentPropsWithoutRef, PropsWithChildren} from 'react'

import {useMinimumUpgradeProduct} from '@src/contexts'

import {resolveGatingFeature, UpgradeLink, UpgradeTarget} from './UpgradeLink'

type UpgradeMessageProps = PropsWithChildren<{
  // align the upgrade button to the start or end of the container
  alignButton?: 'start' | 'end'
  // legacy: to display after "Requires Upgrade" message
  plan?: string
  title?: string
}> &
  Omit<
    ComponentPropsWithoutRef<typeof UpgradeLink>,
    'className' | 'feature' | 'plugin'
  > &
  // Re-stated because Omit over a union collapses it, which would drop the
  // "a feature or a plugin" requirement.
  UpgradeTarget

export function UpgradeMessage(props: UpgradeMessageProps) {
  const {
    alignButton = 'start',
    children,
    plan,
    title,
    ...upgradeLinkProps
  } = props
  const feature = resolveGatingFeature(upgradeLinkProps)
  const product = useMinimumUpgradeProduct(feature)
  const planName = plan ?? product?.name

  // Nothing gates this target, so there is nothing to require. The whole message
  // goes, rather than leaving "Requires Upgrade" sitting above a dead end.
  if (feature === undefined) {
    return null
  }
  return (
    <div className="flex flex-col items-start gap-y-4">
      {title && <h3>{title}</h3>}
      <p className="flex items-center gap-x-1 font-semibold text-gray-700">
        <ArrowCircleUpIcon className="h-5 w-5 text-blue-700" />
        <span>Requires Upgrade</span>
        {planName && <span> to {planName}</span>}
      </p>
      {children && <p>{children}</p>}
      <UpgradeLink
        {...upgradeLinkProps}
        className={clsx('buttonPrimary', alignButton === 'end' && 'self-end')}
      >
        {product ? `Upgrade to ${product.name}` : 'Upgrade'}
      </UpgradeLink>
    </div>
  )
}
